import React, { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Plus, Trash, Truck } from '@phosphor-icons/react';

export default function VehicleCapacityPage() {
  const [rows, setRows] = useState([]);
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [items, setItems] = useState([]);
  const [uoms, setUoms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ vehicle_type: '', item: '', uom: '', capacity: '', notes: '' });
  const [saving, setSaving] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [c, vt, it, u] = await Promise.all([
        api.get('/admin/vehicle-capacities'),
        api.get('/admin/master/vehicle-types'),
        api.get('/admin/master/items'),
        api.get('/admin/master/uoms'),
      ]);
      setRows(c.data || []);
      setVehicleTypes(vt.data || []);
      setItems(it.data || []);
      setUoms(u.data || []);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.vehicle_type || !form.item || !form.uom || !(parseFloat(form.capacity) > 0)) {
      alert('All fields and capacity > 0 are required.');
      return;
    }
    setSaving(true);
    try {
      await api.post('/admin/vehicle-capacities', { ...form, capacity: parseFloat(form.capacity) });
      setForm({ vehicle_type: '', item: '', uom: '', capacity: '', notes: '' });
      refresh();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this capacity row?')) return;
    try { await api.delete(`/admin/vehicle-capacities/${id}`); refresh(); }
    catch (err) { alert(err.response?.data?.detail || 'Delete failed'); }
  };

  return (
    <div className="space-y-4" data-testid="vehicle-capacity-page">
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-3xl md:text-4xl font-black tracking-tighter text-[#0A0A0A] flex items-center gap-2">
            <Truck size={28} weight="duotone" /> Vehicle Capacity Chart
          </h1>
          <p className="text-[#666] text-xs mt-1">
            Item × Vehicle Type capacities used to validate bookings before allocation.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={async () => {
              if (!window.confirm('Recompute capacity flags for every live trip? Useful after editing rows so Trip Monitoring badges refresh immediately.')) return;
              try {
                const { data } = await api.post('/admin/vehicle-capacities/recompute-all');
                alert(`Refreshed ${data.trips_recomputed} live trips.`);
              } catch (err) {
                alert(err.response?.data?.detail || 'Recompute failed');
              }
            }}
            className="text-[10px] uppercase tracking-widest font-bold bg-[#FF8C00]/10 text-[#FF8C00] hover:bg-[#FF8C00] hover:text-white px-3 py-1.5 rounded-full transition-colors"
            data-testid="recompute-capacity-btn"
            title="Refresh capacity-exceeded flags on all live trips after editing capacity rows"
          >
            ↻ Refresh Trip Flags
          </button>
          <span className="text-[10px] uppercase tracking-widest font-bold bg-[#002FA7]/10 text-[#002FA7] px-2 py-1 rounded">
            {rows.length} entries
          </span>
        </div>
      </div>

      {/* Add / Update form */}
      <form
        onSubmit={submit}
        className="bg-white border border-[#E5E5E5] p-4 grid grid-cols-1 md:grid-cols-6 gap-3"
        data-testid="vehicle-capacity-form"
      >
        <div className="md:col-span-1">
          <Label className="text-[10px] uppercase tracking-widest font-bold text-[#666]">Vehicle Type *</Label>
          <select
            value={form.vehicle_type}
            onChange={e => setForm({ ...form, vehicle_type: e.target.value })}
            className="w-full border border-[#E5E5E5] rounded px-2 py-2 text-sm bg-white"
            required
            data-testid="vc-vehicle-type"
          >
            <option value="">Select…</option>
            {vehicleTypes.map(v => <option key={v.id} value={v.name}>{v.name}</option>)}
          </select>
        </div>
        <div className="md:col-span-1">
          <Label className="text-[10px] uppercase tracking-widest font-bold text-[#666]">Item *</Label>
          <select
            value={form.item}
            onChange={e => setForm({ ...form, item: e.target.value })}
            className="w-full border border-[#E5E5E5] rounded px-2 py-2 text-sm bg-white"
            required
            data-testid="vc-item"
          >
            <option value="">Select…</option>
            {items.map(i => <option key={i.id} value={i.name}>{i.name}</option>)}
          </select>
        </div>
        <div className="md:col-span-1">
          <Label className="text-[10px] uppercase tracking-widest font-bold text-[#666]">UOM *</Label>
          <select
            value={form.uom}
            onChange={e => setForm({ ...form, uom: e.target.value })}
            className="w-full border border-[#E5E5E5] rounded px-2 py-2 text-sm bg-white"
            required
            data-testid="vc-uom"
          >
            <option value="">Select…</option>
            {uoms.map(u => <option key={u.id} value={u.name}>{u.name}</option>)}
          </select>
        </div>
        <div className="md:col-span-1">
          <Label className="text-[10px] uppercase tracking-widest font-bold text-[#666]">Capacity *</Label>
          <Input
            type="number"
            step="any"
            value={form.capacity}
            onChange={e => setForm({ ...form, capacity: e.target.value })}
            className="rounded text-sm"
            placeholder="e.g. 16"
            required
            data-testid="vc-capacity"
          />
        </div>
        <div className="md:col-span-1">
          <Label className="text-[10px] uppercase tracking-widest font-bold text-[#666]">Notes</Label>
          <Input
            value={form.notes}
            onChange={e => setForm({ ...form, notes: e.target.value })}
            className="rounded text-sm"
            placeholder="optional"
            data-testid="vc-notes"
          />
        </div>
        <div className="md:col-span-1 flex items-end">
          <Button
            type="submit"
            disabled={saving}
            className="w-full bg-[#002FA7] text-white hover:bg-[#002FA7]/90 hover:text-white rounded-full"
            data-testid="vc-save"
          >
            <Plus size={14} className="mr-1" /> {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
        <p className="md:col-span-6 text-[10px] text-[#666]">
          Existing combo (vehicle type + item + UOM) auto-updates instead of creating a duplicate.
        </p>
      </form>

      {/* Table */}
      <div className="bg-white border border-[#E5E5E5] overflow-x-auto">
        {loading ? (
          <p className="text-center py-12 text-sm text-[#666]">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="text-center py-12 text-sm text-[#666]">No capacity entries yet. Use the form above to add the first row.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-[#F3F3F3]">
                <TableHead className="text-[10px] uppercase tracking-widest">Vehicle Type</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest">Item</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest">UOM</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest text-right">Capacity</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest">Notes</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest">Last Updated By</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest text-center">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map(r => (
                <TableRow key={r.id} data-testid={`vc-row-${r.id}`}>
                  <TableCell className="font-bold text-[#002FA7]">{r.vehicle_type}</TableCell>
                  <TableCell>{r.item}</TableCell>
                  <TableCell>{r.uom}</TableCell>
                  <TableCell className="text-right font-mono font-black">{r.capacity}</TableCell>
                  <TableCell className="text-[#666] text-xs">{r.notes || '—'}</TableCell>
                  <TableCell className="text-xs text-[#666]">{r.updated_by || r.created_by || '—'}</TableCell>
                  <TableCell className="text-center">
                    <button
                      onClick={() => remove(r.id)}
                      className="text-[#FF3333] hover:text-[#cc0000] p-1"
                      data-testid={`vc-delete-${r.id}`}
                      aria-label="Delete"
                    >
                      <Trash size={14} weight="bold" />
                    </button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
